import { useQuery } from '@tanstack/react-query';
import { LoadingState } from '@v2/design-system';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';
import type { CommissionPreview, PloomesDealDetail } from '../types';
import PaymentMethodBadge from '../PaymentMethodBadge';
import DetailCard from './DetailCard';

type Props = {
    dealId: number | string;
    deal?: Pick<
        PloomesDealDetail,
        | 'entry_payment_method'
        | 'entry_payment_method_kind'
        | 'remaining_payment_method'
        | 'remaining_payment_method_kind'
    > | null;
};

export default function DealPaymentMethodsCard({ dealId, deal = null }: Props) {
    const { t } = useI18n();

    const previewQuery = useQuery<CommissionPreview | null>({
        queryKey: ['ploomes-deal-commission-preview', dealId],
        queryFn: async ({ signal }) => {
            const { data } = await api.get<{ data: CommissionPreview | null }>(
                `/ploomes-deals/${dealId}/commission-preview`,
                { signal, timeout: 60_000 },
            );
            return data.data;
        },
        enabled: Boolean(dealId),
        staleTime: 0,
    });

    const preview = previewQuery.data;
    const entryLabel = preview?.entry_payment_method ?? deal?.entry_payment_method ?? null;
    const entryKind = preview?.entry_payment_method_kind ?? deal?.entry_payment_method_kind ?? null;
    const remainingLabel = preview?.remaining_payment_method ?? deal?.remaining_payment_method ?? null;
    const remainingKind =
        preview?.remaining_payment_method_kind ?? deal?.remaining_payment_method_kind ?? null;

    return (
        <DetailCard title={t('v2.ploomes_deals.payment_methods_title')}>
            {previewQuery.isPending && !preview && !deal ? (
                <LoadingState variant="inline" />
            ) : (
                <div className="grid gap-4 sm:grid-cols-2 sm:gap-5">
                    <div className="rounded-2xl border border-gray-100 bg-gray-50/80 px-4 py-4 dark:border-zinc-700 dark:bg-zinc-800/60">
                        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                            {t('v2.ploomes_deals.model_fields.entry_payment_method')}
                        </p>
                        <PaymentMethodBadge label={entryLabel} kind={entryKind} />
                    </div>
                    <div className="rounded-2xl border border-gray-100 bg-gray-50/80 px-4 py-4 dark:border-zinc-700 dark:bg-zinc-800/60">
                        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                            {t('v2.ploomes_deals.model_fields.remaining_payment_method')}
                        </p>
                        <PaymentMethodBadge label={remainingLabel} kind={remainingKind} />
                    </div>
                </div>
            )}
        </DetailCard>
    );
}
